import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertCircle, Camera, Check, Scale } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { KG_TO_LBS, CM_TO_IN } from "@/hooks/useUnitPrefs";

export default function ProgressForm({
  open,
  onOpenChange,
  onSubmit,
  initialData = null,
  useLbs = true,
  useInches = true,
  lastWeightKg = null,
}) {
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    weight: "",
    body_fat_percentage: "",
    chest: "",
    waist: "",
    hips: "",
    arms: "",
    thighs: "",
    notes: "",
    photo_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [confirmedSuspicious, setConfirmedSuspicious] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmedSuspicious(false);
      if (initialData) {
        setForm({
          date: initialData.date ? initialData.date.split("T")[0] : format(new Date(), "yyyy-MM-dd"),
          weight:
            initialData.weight_kg != null
              ? useLbs
                ? (initialData.weight_kg * KG_TO_LBS).toFixed(1)
                : String(initialData.weight_kg)
              : "",
          body_fat_percentage:
            initialData.body_fat_percentage != null ? String(initialData.body_fat_percentage) : "",
          chest:
            initialData.chest_cm != null
              ? useInches
                ? (initialData.chest_cm * CM_TO_IN).toFixed(1)
                : String(initialData.chest_cm)
              : "",
          waist:
            initialData.waist_cm != null
              ? useInches
                ? (initialData.waist_cm * CM_TO_IN).toFixed(1)
                : String(initialData.waist_cm)
              : "",
          hips:
            initialData.hips_cm != null
              ? useInches
                ? (initialData.hips_cm * CM_TO_IN).toFixed(1)
                : String(initialData.hips_cm)
              : "",
          arms:
            initialData.arms_cm != null
              ? useInches
                ? (initialData.arms_cm * CM_TO_IN).toFixed(1)
                : String(initialData.arms_cm)
              : "",
          thighs:
            initialData.thighs_cm != null
              ? useInches
                ? (initialData.thighs_cm * CM_TO_IN).toFixed(1)
                : String(initialData.thighs_cm)
              : "",
          notes: initialData.notes || "",
          photo_url: initialData.photo_url || "",
        });
      } else {
        setForm({
          date: format(new Date(), "yyyy-MM-dd"),
          weight: "",
          body_fat_percentage: "",
          chest: "",
          waist: "",
          hips: "",
          arms: "",
          thighs: "",
          notes: "",
          photo_url: "",
        });
      }
    }
  }, [open, initialData, useLbs, useInches]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setForm((f) => ({ ...f, photo_url: file_url }));
    } catch {
      // non-fatal
    } finally {
      setUploading(false);
    }
  };

  // Detect suspicious weight fluctuation compared to last weight
  const enteredWeightNum = parseFloat(form.weight);
  const enteredWeightKg = !isNaN(enteredWeightNum)
    ? useLbs
      ? enteredWeightNum / KG_TO_LBS
      : enteredWeightNum
    : null;

  const isSuspicious =
    !initialData &&
    enteredWeightKg != null &&
    lastWeightKg != null &&
    Math.abs(enteredWeightKg - lastWeightKg) > (useLbs ? 25 / KG_TO_LBS : 12);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isSuspicious && !confirmedSuspicious) {
      setConfirmedSuspicious(true);
      return;
    }

    const cleaned = {
      date: form.date,
    };

    if (form.weight !== "") {
      const num = parseFloat(form.weight);
      cleaned.weight_kg = useLbs ? +(num / KG_TO_LBS).toFixed(2) : num;
    }
    if (form.body_fat_percentage !== "") {
      cleaned.body_fat_percentage = parseFloat(form.body_fat_percentage);
    }
    if (form.chest !== "") {
      const num = parseFloat(form.chest);
      cleaned.chest_cm = useInches ? +(num / CM_TO_IN).toFixed(2) : num;
    }
    if (form.waist !== "") {
      const num = parseFloat(form.waist);
      cleaned.waist_cm = useInches ? +(num / CM_TO_IN).toFixed(2) : num;
    }
    if (form.hips !== "") {
      const num = parseFloat(form.hips);
      cleaned.hips_cm = useInches ? +(num / CM_TO_IN).toFixed(2) : num;
    }
    if (form.arms !== "") {
      const num = parseFloat(form.arms);
      cleaned.arms_cm = useInches ? +(num / CM_TO_IN).toFixed(2) : num;
    }
    if (form.thighs !== "") {
      const num = parseFloat(form.thighs);
      cleaned.thighs_cm = useInches ? +(num / CM_TO_IN).toFixed(2) : num;
    }
    if (form.notes.trim()) {
      cleaned.notes = form.notes.trim();
    }
    if (form.photo_url) {
      cleaned.photo_url = form.photo_url;
    }

    onSubmit(cleaned);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[#131F31] border border-[#233148] text-white rounded-2xl p-6">
        <DialogHeader>
          <div className="w-10 h-10 rounded-xl bg-[#36D0FF]/10 text-[#36D0FF] flex items-center justify-center mb-2">
            <Scale className="w-5 h-5" />
          </div>
          <DialogTitle className="text-xl font-bold text-white font-display">
            {initialData ? "Edit Check-in" : "Log Progress"}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Record your weight and measurements to keep your trends accurate.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Check-in Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="bg-[#0C1424] border-[#233148] text-white mt-1"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">
                Weight ({useLbs ? "lbs" : "kg"})
              </Label>
              <Input
                type="number"
                step="0.1"
                value={form.weight}
                onChange={(e) => {
                  setForm({ ...form, weight: e.target.value });
                  setConfirmedSuspicious(false);
                }}
                placeholder={useLbs ? "176.4" : "80.0"}
                className="bg-[#0C1424] border-[#233148] text-white mt-1 text-base font-semibold"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">Body Fat %</Label>
              <Input
                type="number"
                step="0.1"
                min="3"
                max="60"
                value={form.body_fat_percentage}
                onChange={(e) => setForm({ ...form, body_fat_percentage: e.target.value })}
                placeholder="16.5"
                className="bg-[#0C1424] border-[#233148] text-white mt-1 text-base font-semibold"
              />
            </div>
          </div>

          {isSuspicious && !confirmedSuspicious && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-300">Noticeable weight change detected</p>
                <p className="mt-0.5 text-white/70">
                  This weight differs noticeably from your last recorded check-in (
                  {useLbs ? (lastWeightKg * KG_TO_LBS).toFixed(1) + " lbs" : lastWeightKg.toFixed(1) + " kg"}).
                  Tap <strong>Confirm & Save</strong> to keep this entry.
                </p>
              </div>
            </div>
          )}

          <div className="pt-1">
            <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
              Body Measurements ({useInches ? "inches" : "cm"})
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <div>
                <Label className="text-[11px] text-white/60">Waist</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.waist}
                  onChange={(e) => setForm({ ...form, waist: e.target.value })}
                  placeholder={useInches ? "32.7" : "83"}
                  className="bg-[#0C1424] border-[#233148] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] text-white/60">Chest</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.chest}
                  onChange={(e) => setForm({ ...form, chest: e.target.value })}
                  placeholder={useInches ? "40.0" : "101"}
                  className="bg-[#0C1424] border-[#233148] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] text-white/60">Hips</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.hips}
                  onChange={(e) => setForm({ ...form, hips: e.target.value })}
                  placeholder={useInches ? "38.0" : "96"}
                  className="bg-[#0C1424] border-[#233148] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] text-white/60">Arms</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.arms}
                  onChange={(e) => setForm({ ...form, arms: e.target.value })}
                  placeholder={useInches ? "14.5" : "37"}
                  className="bg-[#0C1424] border-[#233148] text-white h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] text-white/60">Thighs</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.thighs}
                  onChange={(e) => setForm({ ...form, thighs: e.target.value })}
                  placeholder={useInches ? "22.5" : "57"}
                  className="bg-[#0C1424] border-[#233148] text-white h-9 text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Felt great this morning, fasted check-in"
              className="bg-[#0C1424] border-[#233148] text-white text-sm mt-1"
              rows={2}
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-white/60">Progress Photo (Optional)</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0C1424] border border-[#233148] text-white/80 hover:text-white hover:border-[#36D0FF] text-xs cursor-pointer transition-colors">
                <Camera className="w-4 h-4 text-[#36D0FF]" />
                <span>{form.photo_url ? "Change photo" : "Select photo"}</span>
                <input type="file" accept="image/*" onChange={handlePhoto} disabled={uploading} className="hidden" />
              </label>
              {uploading && <span className="text-xs text-white/50 animate-pulse">Uploading…</span>}
              {form.photo_url && !uploading && (
                <span className="text-xs text-emerald-400 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Photo attached
                </span>
              )}
            </div>
          </div>

          <div className="pt-2 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/60 hover:text-white hover:bg-white/5 flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#36D0FF] hover:bg-[#36D0FF]/90 text-[#0C1424] font-semibold flex-1"
            >
              {isSuspicious && !confirmedSuspicious
                ? "Confirm & Save"
                : initialData
                ? "Update Check-in"
                : "Save Check-in"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}